import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  Divider,
  DownloadIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  LinkIcon,
  NoData,
  PopConfirm,
  Popover,
  ProformaIcon,
  RedirectIcon,
  SendIcon,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useState } from "react";
import {
  useDeleteProformaMutation,
  useGetProformaListQuery,
} from "store/features/Revenue/ProformaApi";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { moneyCommaSeparator, numberToCurrency } from "utils/MathFunction";
import { UserRoleTypes } from "utils/common";
import {
  extraColumnsField,
  handleDownloadFile,
  matchDate,
} from "utils/helpers";
import ProformaSendModal from "./ProformaSendModal";
import ProformaToInvoiceModal from "./ProformaToInvoiceModal";
import { updateFilter } from "store/slices/tableSlice";
import { FinancialYearDropdown, financialYearFilter } from "../utils";

interface Props {
  name?: string;
  tableName?: string;
  portfolioId?: string;
}

const Proforma = (props: Props) => {
  const userData = useAppSelector((state) => state?.user?.userData);
  const commonFilter = useAppSelector((state) => state?.table?.commonFilter);
  const dispatch = useAppDispatch();

  const { name, tableName, portfolioId } = props;

  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [openConvertInvoiceModal, setOpenConvertInvoiceModal] = useState(false);
  const [proformaDetails, setProformaDetails] = useState(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openSendProformaModal, setOpenSendProformaModal] = useState({
    open: false,
    type: "",
  });

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const requestBody = useGenerateQueryParams(name || "proformaInvoice", {
    portfolio_id: portfolioId,
  });

  const { data, isFetching } = useGetProformaListQuery(requestBody);

  const [deleteProforma] = useDeleteProformaMutation();

  const updateFYFilter = (filterName: string, val: any) => {
    const targetName = filterName || "proformaInvoice";
    dispatch(
      updateFilter({
        name: targetName,
        filter: {
          customFilter: {
            financialYear: val?.value,
            start_date: val.dates.startDate,
            end_date: val.dates.endDate,
          },
        },
      }),
    );
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Proforma No (${data?.totalCount || 0})`,
        "proformaNumber",
        150,
        1,
      ),
    },
    {
      ...extraColumnsField("Date", "date", 120, 1),
      type: "date",
      valueFormatter: (value) => matchDate(value.value),
    },
    {
      ...extraColumnsField("Portfolio", "clientName", 250, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Taxable Amount (₹)", "taxableFee", 170, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.taxableFee),
      type: "number",
    },
    {
      ...extraColumnsField("Final Amount (₹)", "totalFee", 150, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.totalFee),
      type: "number",
    },
    {
      ...extraColumnsField("Fee Plan Name", "feePlanName", 150, 1),
    },
    {
      ...extraColumnsField("Invoice", "isInvoice", 100, 1),
      renderCell: (row) => (
        <StatusField statusString={row?.row?.isInvoice.toString()} />
      ),
    },
    {
      ...extraColumnsField("Actions", "action", 220, 1),
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) => (
        <Grid2
          container
          size={12}
          gap={1}
          display={"flex"}
          justifyContent={"flex-end"}
        >
          <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
            <GKButton
              disabled={params?.row?.isForfeit || params?.row?.isInvoice}
              onClick={() => {
                setProformaDetails(params?.row);
                setOpenConvertInvoiceModal(true);
              }}
            >
              Convert to Invoice
            </GKButton>
          </Show.When>
          <IconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setProformaDetails(params?.row);
              setAnchorEl(event.currentTarget);
            }}
          >
            <KebabMenu2Icon />
          </IconButton>
        </Grid2>
      ),
    },
  ];

  const deleteDetails = {
    "Client Name": proformaDetails?.clientName,
    "Proforma Invoice No:": proformaDetails?.proformaNumber,
    Amount: numberToCurrency(proformaDetails?.totalFee),
    "Creation Date:": matchDate(proformaDetails?.date),
  };

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        maxHeight={"calc(100vh - 317px)"}
        totalCount={data?.totalCount || 0}
        name={name || "proformaInvoice"}
        tableName={tableName || "proforma_invoice"}
        headerComponent={
          <FinancialYearDropdown
            updateFilter={updateFYFilter}
            tableKey={name || "proformaInvoice"}
            selectedValue={
              financialYearFilter?.find(
                (dataItem) =>
                  dataItem.value ===
                  (name
                    ? commonFilter?.[name]?.customFilter?.financialYear
                    : commonFilter?.proformaInvoice?.customFilter
                        ?.financialYear),
              )?.name
            }
          />
        }
        emptyComponent={
          <NoData
            text="Proforma List is Empty"
            customImg={
              <ProformaIcon height={80} width={80} color="var(--neutral-100)" />
            }
            additionalComponent={
              <Grid2
                container
                spacing={2}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                flexDirection={"column"}
              >
                <Grid2 size={12}>
                  <Typography fontWeight={600} color="var(--neutral-700)">
                    How to Create a Proforma:
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography fontWeight={500} color="var(--neutral-500)">
                    1) Go to the{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: "var(var(--neutral-700))",
                      }}
                    >
                      Fee Calculation
                    </span>{" "}
                    tab
                  </Typography>
                  <Typography fontWeight={500} color="var(--neutral-500)">
                    2) Click on{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: "var(var(--neutral-700))",
                      }}
                    >
                      + New Fee Collection
                    </span>
                  </Typography>
                  <Typography fontWeight={500} color="var(--neutral-500)">
                    3) Add all required details
                  </Typography>
                  <Typography fontWeight={500} color="var(--neutral-500)">
                    2) Save it as a{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: "var(var(--neutral-700))",
                      }}
                    >
                      Proforma
                    </span>
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <GKButton
                    endIcon={<RedirectIcon />}
                    onClick={() =>
                      dispatch(
                        TAB({ tabKey: "tabValueOfRevenue", tabValue: 3 }),
                      )
                    }
                  >
                    Go to Fee Calculation Tab
                  </GKButton>
                </Grid2>
              </Grid2>
            }
          />
        }
      />

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              width: "190px",
              borderRadius: "8px",
              p: "8px 0",
              border: "1px solid var(--neutral-100)",
              bgcolor: "var(--white)",
              boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
            },
          },
        }}
      >
        <Grid2 container>
          <Grid2
            size={12}
            p={"12px"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            sx={{ cursor: "pointer" }}
          >
            <DownloadIcon />
            <Typography
              fontSize={12}
              fontWeight={600}
              color="var(--grey-900)"
              onClick={() => {
                handleDownloadFile({
                  link: proformaDetails?.proformaPdf,
                  name: proformaDetails?.proformaNumber,
                });
                setAnchorEl(null);
              }}
            >
              Download
            </Typography>
          </Grid2>
          <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2
              size={12}
              p={"12px"}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setOpenSendProformaModal({
                  open: true,
                  type: "SEND_PROFORMA_ONLY",
                });
                setAnchorEl(null);
              }}
            >
              <SendIcon />
              <Typography
                fontSize={12}
                fontWeight={600}
                color="var(--grey-900)"
              >
                Send Proforma Only
              </Typography>
            </Grid2>
            <Grid2
              size={12}
              p={"12px"}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setOpenSendProformaModal({
                  open: true,
                  type: "SEND_PROFORMA_WITH_PAYMENT_LINK",
                });
                setAnchorEl(null);
              }}
            >
              <LinkIcon />
              <Typography
                fontSize={12}
                fontWeight={600}
                color="var(--grey-900)"
              >
                Send with Payment Link
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <Divider />
            </Grid2>

            <Grid2
              size={12}
              p={"12px"}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              color={"var(--warning-700)"}
              sx={{ cursor: "pointer" }}
              onClick={(event) => {
                setConfirmationModal(event.currentTarget);
                setAnchorEl(null);
              }}
            >
              <DeleteIcon />
              <Typography fontSize={12} fontWeight={600}>
                Delete
              </Typography>
            </Grid2>
          </Show.When>
        </Grid2>
      </Popover>

      <Show.When isTrue={openSendProformaModal?.open}>
        <ProformaSendModal
          open={openSendProformaModal}
          setOpen={setOpenSendProformaModal}
          proformaDetails={proformaDetails}
        />
      </Show.When>

      <Show.When isTrue={openConvertInvoiceModal}>
        <ProformaToInvoiceModal
          open={openConvertInvoiceModal}
          setOpen={setOpenConvertInvoiceModal}
          proformaDetails={proformaDetails}
        />
      </Show.When>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={null}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          modalTitle="Delete Proforma Invoice?"
          message="This action will permanently delete the selected proforma invoice. You can’t undo this."
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          onSuccess={async () => {
            try {
              await deleteProforma(proformaDetails?.id).unwrap();
            } catch (err) {
              console.error("Delete proforma failed:", err);
            }
          }}
          extraComponent={
            <Grid2 container spacing={1}>
              {Object.entries(deleteDetails).map(([label, value], index) => (
                <Grid2 key={index} container size={12} display="flex">
                  <Grid2 size={5}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-500)"
                    >
                      {label}
                    </Typography>
                  </Grid2>
                  <Grid2 size={7}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-700)"
                    >
                      {value || "-"}
                    </Typography>
                  </Grid2>
                </Grid2>
              ))}
            </Grid2>
          }
        />
      </Show.When>
    </>
  );
};

export default Proforma;
